/**
 * Reading the parent/child hierarchy out of an entity registry.
 *
 * `PanelConfig.entities` maps each entity id to a declaration that optionally
 * names another entity as its `parent`. These pure functions are the one place
 * that interprets those references — consumed by the schema's validation
 * refinements, by the Babylon systems that accumulate absolute positions, and by
 * the settings sidebar that renders the tree.
 *
 * Typed structurally against {@link HierarchyEntity} and generic over the
 * declaration rather than importing `EntityDeclaration` from
 * `./three-d-schemas`, because `three-d-schemas` imports `findParentCycle` for
 * its validation refinement and a mutual import would be a cycle.
 *
 * Every function here tolerates a malformed hierarchy rather than throwing. A
 * `parent` naming an entity that does not exist is treated as no parent, and a
 * cycle is broken at whichever member is reached first — so the members stay
 * visible and every traversal terminates, but where the break falls is
 * arbitrary. Rejecting a cycle is {@link findParentCycle}'s job and happens at
 * the schema boundary; the runtime consumers have to keep drawing something
 * sensible regardless.
 */

/**
 * The only field of an entity declaration this module reads. Structurally
 * identical to `EntityDeclaration`, which is what callers actually pass.
 *
 * Ids are `string` throughout, matching `EntityID` in `./three-d-schemas`.
 */
export interface HierarchyEntity {
  parent?: string | undefined;
}

/** An entity plus its place in the hierarchy, for rendering an indented list. */
export interface EntityTreeNode<E extends HierarchyEntity> {
  id: string;
  entity: E;
  /** 0 for a root, 1 for its children, and so on. */
  depth: number;
  children: EntityTreeNode<E>[];
}

/**
 * The parent this entity actually has, ignoring references that cannot be
 * followed.
 *
 * Returns `undefined` for a root, for a `parent` that names a missing entity,
 * and for an entity that names itself. A dangling reference is deliberately not
 * an error: a workspace should load and show the entity as a root rather than
 * refuse to open, and a Layout round-trip can drop an ancestor while keeping its
 * child.
 */
export function resolveParent<E extends HierarchyEntity>(
  entities: Record<string, E>,
  id: string,
): string | undefined {
  const parent = entities[id]?.parent;
  if (parent === undefined || parent === id || !entities[parent]) {
    return undefined;
  }
  return parent;
}

/**
 * Every entity reachable by following `parent` from `id`, nearest first, with
 * `id` itself at the front. Empty if `id` names no entity.
 *
 * Used to accumulate a chain of local positions into an absolute one. Stops if
 * it revisits an entity, so a cycle yields a finite prefix instead of hanging.
 */
export function ancestorChain<E extends HierarchyEntity>(
  entities: Record<string, E>,
  id: string,
): string[] {
  const chain: string[] = [];
  const seen = new Set<string>();
  let current: string | undefined = entities[id] ? id : undefined;

  while (current !== undefined && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    current = resolveParent(entities, current);
  }

  return chain;
}

/**
 * Every descendant of `id`, at any depth. Excludes `id` itself.
 *
 * Three callers: excluding invalid choices from the parent picker (an entity
 * cannot be parented to its own descendant), collecting a subtree to delete, and
 * invalidating derived caches when an ancestor's data reloads.
 */
export function descendantIds<E extends HierarchyEntity>(
  entities: Record<string, E>,
  id: string,
): Set<string> {
  const childrenByParent = groupByParent(entities);
  const descendants = new Set<string>();
  const queue = [...(childrenByParent.get(id) ?? [])];

  while (queue.length > 0) {
    const next = queue.pop() as string;
    if (descendants.has(next) || next === id) {
      continue;
    }
    descendants.add(next);
    queue.push(...(childrenByParent.get(next) ?? []));
  }

  return descendants;
}

/**
 * Entity ids ordered so that every entity follows its parent.
 *
 * Consumers accumulate a child's absolute position from its parent's, so they
 * depend on the parent having been resolved first. Insertion order is preserved
 * among siblings, which keeps the order stable across unrelated edits.
 *
 * Entities inside a cycle are emitted last, as roots. A cycle cannot be ordered,
 * but dropping those entities would make them invisible rather than merely
 * misplaced.
 */
export function topologicalOrder<E extends HierarchyEntity>(
  entities: Record<string, E>,
): string[] {
  const childrenByParent = groupByParent(entities);
  const ordered: string[] = [];
  const emitted = new Set<string>();

  const emitSubtree = (id: string) => {
    if (emitted.has(id)) {
      return;
    }
    emitted.add(id);
    ordered.push(id);
    for (const child of childrenByParent.get(id) ?? []) {
      emitSubtree(child);
    }
  };

  for (const id of Object.keys(entities)) {
    if (resolveParent(entities, id) === undefined) {
      emitSubtree(id);
    }
  }

  // Whatever is left is in a cycle, so it has no root to descend from.
  for (const id of Object.keys(entities)) {
    if (!emitted.has(id)) {
      emitted.add(id);
      ordered.push(id);
    }
  }

  return ordered;
}

/**
 * The hierarchy as a tree of roots, for rendering.
 *
 * Siblings keep their insertion order from the record. Every entity appears
 * exactly once, including the members of a cycle: the first one reached becomes
 * a root and the rest nest beneath it, so the cycle renders as a finite chain
 * the user can still edit to break it.
 */
export function buildEntityTree<E extends HierarchyEntity>(
  entities: Record<string, E>,
): EntityTreeNode<E>[] {
  const childrenByParent = groupByParent(entities);
  const placed = new Set<string>();

  const buildNode = (id: string, depth: number): EntityTreeNode<E> => {
    placed.add(id);
    return {
      id,
      entity: entities[id],
      depth,
      children: (childrenByParent.get(id) ?? [])
        .filter((child) => !placed.has(child))
        .map((child) => buildNode(child, depth + 1)),
    };
  };

  const roots: EntityTreeNode<E>[] = [];
  for (const id of Object.keys(entities)) {
    if (resolveParent(entities, id) === undefined) {
      roots.push(buildNode(id, 0));
    }
  }
  for (const id of Object.keys(entities)) {
    if (!placed.has(id)) {
      roots.push(buildNode(id, 0));
    }
  }

  return roots;
}

/**
 * One cycle in the parent references, or `null` if there is none.
 *
 * Returns the cycle's members in walk order starting from the entity the walk
 * re-entered, so an error message can name them. Self-parenting counts as a
 * cycle of one.
 *
 * Unlike the rest of this module, this reads `entity.parent` directly rather
 * than going through {@link resolveParent} — the latter reports a self-parent as
 * "no parent" precisely so the runtime can keep going, which would hide the very
 * thing this function exists to find.
 */
export function findParentCycle<E extends HierarchyEntity>(
  entities: Record<string, E>,
): string[] | null {
  const settled = new Set<string>();

  for (const start of Object.keys(entities)) {
    if (settled.has(start)) {
      continue;
    }
    const path: string[] = [];
    const onPath = new Set<string>();
    let current: string | undefined = start;

    while (current !== undefined && entities[current]) {
      if (onPath.has(current)) {
        return path.slice(path.indexOf(current));
      }
      if (settled.has(current)) {
        break;
      }
      path.push(current);
      onPath.add(current);
      current = entities[current].parent;
    }

    for (const id of path) {
      settled.add(id);
    }
  }

  return null;
}

/**
 * Children keyed by parent id, in the record's insertion order.
 *
 * Keyed on the *resolved* parent, so a dangling or self reference leaves the
 * entity out of every child list and it reads as a root throughout this module.
 */
function groupByParent<E extends HierarchyEntity>(
  entities: Record<string, E>,
): Map<string, string[]> {
  const childrenByParent = new Map<string, string[]>();

  for (const id of Object.keys(entities)) {
    const parent = resolveParent(entities, id);
    if (parent === undefined) {
      continue;
    }
    const siblings = childrenByParent.get(parent);
    if (siblings) {
      siblings.push(id);
    } else {
      childrenByParent.set(parent, [id]);
    }
  }

  return childrenByParent;
}
